import { useEditor, useEditorState, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import { Placeholder } from "@tiptap/extensions";
import { Box, ButtonGroup, IconButton, Tooltip } from "@chakra-ui/react";
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaListUl,
  FaListOl,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaAlignJustify,
} from "react-icons/fa";
import { useEffect } from "react";

// Values are stored as TipTap JSON strings; older ones are plain text
const toDoc = (value) => {
  try {
    if (typeof value === "string" && value.trim() !== "") {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === "object") return parsed;
    }
  } catch {
    // not JSON - fall through and treat as plain text
  }
  return {
    type: "doc",
    content: [{ type: "paragraph", content: value ? [{ type: "text", text: String(value) }] : [] }],
  };
};

const MiniRichTextEditor = ({ value, onChange, placeholder = "Enter default value...", minHeight = "150px" }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Placeholder.configure({ placeholder }),
    ],
    // A JSON string passed as content would be parsed as HTML, so pass the parsed document
    content: toDoc(value),
    onUpdate: ({ editor }) => {
      const json = editor.getJSON();
      onChange(JSON.stringify(json));
    },
  });

  // Update editor when value changes externally
  useEffect(() => {
    if (editor && value !== JSON.stringify(editor.getJSON())) {
      // Don't emit an update: that would call onChange with the value we were just given
      editor.commands.setContent(toDoc(value), { emitUpdate: false });
    }
  }, [value, editor]);

  // TipTap 3 doesn't re-render on every transaction, so subscribe to the toolbar's active states
  const active = useEditorState({
    editor,
    selector: ({ editor }) => (!editor || editor.isDestroyed) ? null : ({
      bold: editor.isActive("bold"),
      italic: editor.isActive("italic"),
      underline: editor.isActive("underline"),
      bulletList: editor.isActive("bulletList"),
      orderedList: editor.isActive("orderedList"),
      alignLeft: editor.isActive({ textAlign: "left" }),
      alignCenter: editor.isActive({ textAlign: "center" }),
      alignRight: editor.isActive({ textAlign: "right" }),
      alignJustify: editor.isActive({ textAlign: "justify" }),
    }),
  });

  if (!editor || !active) {
    return null;
  }

  return (
    <Box>
      {/* Toolbar */}
      <Box
        borderWidth="1px"
        borderBottom="none"
        borderRadius="md"
        borderBottomRadius="none"
        p={2}
        bg="gray.50"
        display="flex"
        gap={2}
        flexWrap="wrap">
        {/* Text Formatting */}
        <ButtonGroup size="sm" isAttached variant="outline">
          <Tooltip label="Bold">
            <IconButton
              icon={<FaBold />}
              onClick={() => editor.chain().focus().toggleBold().run()}
              isActive={active.bold}
              bg={active.bold ? "blue.100" : "white"}
              aria-label="Bold"
            />
          </Tooltip>
          <Tooltip label="Italic">
            <IconButton
              icon={<FaItalic />}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              isActive={active.italic}
              bg={active.italic ? "blue.100" : "white"}
              aria-label="Italic"
            />
          </Tooltip>
          <Tooltip label="Underline">
            <IconButton
              icon={<FaUnderline />}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              isActive={active.underline}
              bg={active.underline ? "blue.100" : "white"}
              aria-label="Underline"
            />
          </Tooltip>
        </ButtonGroup>

        {/* Lists */}
        <ButtonGroup size="sm" isAttached variant="outline">
          <Tooltip label="Bullet List">
            <IconButton
              icon={<FaListUl />}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              isActive={active.bulletList}
              bg={active.bulletList ? "blue.100" : "white"}
              aria-label="Bullet List"
            />
          </Tooltip>
          <Tooltip label="Numbered List">
            <IconButton
              icon={<FaListOl />}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              isActive={active.orderedList}
              bg={active.orderedList ? "blue.100" : "white"}
              aria-label="Numbered List"
            />
          </Tooltip>
        </ButtonGroup>

        {/* Text Alignment */}
        <ButtonGroup size="sm" isAttached variant="outline">
          <Tooltip label="Align Left">
            <IconButton
              icon={<FaAlignLeft />}
              onClick={() => editor.chain().focus().setTextAlign("left").run()}
              isActive={active.alignLeft}
              bg={active.alignLeft ? "blue.100" : "white"}
              aria-label="Align Left"
            />
          </Tooltip>
          <Tooltip label="Align Center">
            <IconButton
              icon={<FaAlignCenter />}
              onClick={() => editor.chain().focus().setTextAlign("center").run()}
              isActive={active.alignCenter}
              bg={active.alignCenter ? "blue.100" : "white"}
              aria-label="Align Center"
            />
          </Tooltip>
          <Tooltip label="Align Right">
            <IconButton
              icon={<FaAlignRight />}
              onClick={() => editor.chain().focus().setTextAlign("right").run()}
              isActive={active.alignRight}
              bg={active.alignRight ? "blue.100" : "white"}
              aria-label="Align Right"
            />
          </Tooltip>
          <Tooltip label="Justify">
            <IconButton
              icon={<FaAlignJustify />}
              onClick={() => editor.chain().focus().setTextAlign("justify").run()}
              isActive={active.alignJustify}
              bg={active.alignJustify ? "blue.100" : "white"}
              aria-label="Justify"
            />
          </Tooltip>
        </ButtonGroup>
      </Box>

      {/* Editor Content */}
      <Box
        borderWidth="1px"
        borderRadius="md"
        borderTopRadius="none"
        p={3}
        minH={minHeight}
        bg="white"
        sx={{
          ".ProseMirror": {
            outline: "none",
            minHeight: minHeight,
            "& p.is-editor-empty:first-of-type::before": {
              content: "attr(data-placeholder)",
              color: "gray.400",
              float: "left",
              height: 0,
              pointerEvents: "none",
            },
          },
        }}>
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

export default MiniRichTextEditor;
